// ส่ง session ต่อให้ Inbox Center ที่ฝังเป็น iframe
//
// หน้าที่ฝังของเขา (comments.html / analytics.html / messages) มี auth.js เป็นตัวกั้น
// ก่อน render — อ่าน **localStorage['wz_session']** แล้วเช็ค expiration ถ้าไม่มีก็เด้ง
// ไปหน้า login ของเขาเองในกรอบ ซึ่งเป็นอาการที่เจอ: shell login แล้วแต่ในกรอบยังขอ login
//
// ของเราใช้คนละ key (localStorage['wazzup_session']) จึงต้องคัดลอกไปวางให้ตรง
// รูปแบบที่เขาอ่าน · ทำได้เพราะกรอบเป็น same-origin (ผ่าน reverse proxy /inbox → เขา)
// ถ้าวันหนึ่งเลิก proxy แล้วฝังข้ามโดเมน วิธีนี้จะใช้ไม่ได้ ต้องส่ง token ทาง URL แทน
// เหมือนที่ทำกับ KOL และ Biddable
//
// ฝั่งเขาเช็คแค่ exp ของ JWT ไม่ได้ verify ลายเซ็นและไม่ได้ถาม Wazzup — token จาก IAM
// จึงใช้ได้ (ต่างจาก KOL ที่ต้องแก้ให้รับ IAM ก่อน)
import type { WazzupSession } from "@features/auth/lib/session";

/** key และรูปร่างนี้เป็นของ Inbox Center (public/auth.js) — เปลี่ยนตามเขาเท่านั้น */
const KEY = "wz_session";

type InboxSession = {
  access_token: string;
  expiration: string;
  idp: "iam" | "wazzup";
  user: Record<string, unknown>;
};

const read = (): InboxSession | null => {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "null") as InboxSession | null;
  } catch {
    return null;
  }
};

/**
 * ทำให้ session ของ Inbox Center ตรงกับของ shell
 *
 * ไม่มี session = ลบของเขาทิ้งด้วย ไม่งั้น logout แล้วกรอบยังใช้งานได้ต่อ
 * มี session แต่ token เดิม = ไม่เขียนทับ เพราะของเขาอาจมีโปรไฟล์ครบกว่า
 * (เขาไปดึง /api/auth/profile มา merge เองหลัง login)
 */
export function syncInboxSession(session: WazzupSession | null): void {
  try {
    if (!session?.access_token) {
      if (read()) localStorage.removeItem(KEY);
      return;
    }
    // ไม่มีวันหมดอายุ = auth.js ของเขาถือว่าหมดอายุทันที เขียนไปก็ไม่มีประโยชน์
    if (!session.expiration) return;

    const current = read();
    if (current?.access_token === session.access_token) return;

    const next: InboxSession = {
      access_token: session.access_token,
      expiration: session.expiration,
      // ใช้ตอน logout ฝั่งเขาว่าต้องล้าง cookie ของ IAM ด้วยไหม
      idp: session.via === "sso" ? "iam" : "wazzup",
      user: {
        empThaiName: session.empThaiName,
        empEngName: session.empEngName,
        nickName: session.nickName,
        positionName: session.positionName,
        departmentName: session.departmentName,
        photo: session.profileURL || "",
        roles: session.roles ?? [],
      },
    };
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* localStorage ใช้ไม่ได้ (โหมดส่วนตัวบางเบราว์เซอร์) — กรอบจะขอ login เอง */
  }
}
